import {ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {PageEvent} from '@angular/material/paginator';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {InsuranceService, BenefitDomain} from '../../Services/insurance.service';
import {AuthService} from '../../auth/AuthService';
import {
  AuditedGroupPlan,
  AuditedGroupPlanDetail,
  AuditedRepositoryPage
} from '../../Model/audited-repository.model';

interface AuditedDetailField {
  label: string;
  value: string;
}

interface AuditedDetailRecord {
  title: string;
  fields: AuditedDetailField[];
}

interface AuditedDetailSection {
  title: string;
  records: AuditedDetailRecord[];
}

@Component({
  selector: 'pm-repository',
  templateUrl: './repository.component.html',
  styleUrls: ['./repository.component.css']
})
export class RepositoryComponent implements OnInit, OnDestroy {
  tableData: AuditedGroupPlan[] = [];

  groupNumber = '';
  planName = '';
  benefitDomain: BenefitDomain | '' = '';
  filtersExpanded = false;

  // The connected overlay renders outside the table panel's overflow:hidden area.
  readonly filterOverlayPositions: ConnectedPosition[] = [
    {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8},
    {originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8}
  ];

  private appliedGroupNumber = '';
  private appliedPlanName = '';
  private appliedBenefitDomain: BenefitDomain | '' = '';

  get activeFilterCount(): number {
    return Number(!!this.appliedGroupNumber) + Number(!!this.appliedPlanName) + Number(!!this.appliedBenefitDomain);
  }

  pageIndex = 0; // The audit repository API and MatPaginator both use 0-based pages.
  pageSize = 25;
  totalRecord = 0;
  totalPagesArray: number[] = [];
  loading = false;
  errorMessage = '';

  detailsVisible = false;
  detailsLoading = false;
  detailsError = '';
  selectedPlan: AuditedGroupPlanDetail | null = null;
  selectedGroupId: number | null = null;
  detailSections: AuditedDetailSection[] = [];

  private currentClientId: number | null = null;
  private deepLinkGroupId: number | null = null;
  private clientSubscription: Subscription;
  private listSubscription: Subscription;
  private detailsSubscription: Subscription;

  constructor(
    private insuranceService: InsuranceService,
    public authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // The endpoint requires audit-view, even for users who can see other reports.
    if (!this.authService.hasRole('audit-view')) {
      this.errorMessage = 'You need the audit-view role to access audited plans.';
      return;
    }

    const routeId = Number(this.route.snapshot.paramMap.get('id'));
    this.deepLinkGroupId = Number.isFinite(routeId) && routeId > 0 ? routeId : null;

    this.clientSubscription = this.authService.clientIdSubject.subscribe((clientId: number | null) => {
      // A client switch may originate outside Angular's zone (e.g. navigation events).
      this.ngZone.run(() => {
        this.currentClientId = clientId;
        this.groupNumber = '';
        this.planName = '';
        this.benefitDomain = '';
        this.appliedGroupNumber = '';
        this.appliedPlanName = '';
        this.appliedBenefitDomain = '';
        this.closeFilters();
        this.pageIndex = 0;
        this.closeDetails(false);
        this.loadRepository();

        // Existing repository/details links now refer to an audited group, not a patient.
        if (this.deepLinkGroupId && clientId) {
          this.openDetailsById(this.deepLinkGroupId);
        }
        this.cdr.markForCheck();
      });
    });
  }

  ngOnDestroy(): void {
    if (this.clientSubscription) {
      this.clientSubscription.unsubscribe();
    }
    if (this.listSubscription) {
      this.listSubscription.unsubscribe();
    }
    if (this.detailsSubscription) {
      this.detailsSubscription.unsubscribe();
    }
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  closeFilters(): void {
    this.filtersExpanded = false;
  }

  applySearch(): void {
    this.appliedGroupNumber = this.groupNumber.trim();
    this.appliedPlanName = this.planName.trim();
    this.appliedBenefitDomain = this.benefitDomain;
    this.closeFilters();
    this.pageIndex = 0;
    this.loadRepository();
  }

  clearSearch(): void {
    this.groupNumber = '';
    this.planName = '';
    this.benefitDomain = '';
    this.applySearch();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadRepository();
  }

  jumpToPage(pageIndex: number): void {
    if (pageIndex < 0 || pageIndex >= this.totalPagesArray.length) {
      return;
    }
    this.pageIndex = pageIndex;
    this.loadRepository();
  }

  loadRepository(): void {
    if (this.listSubscription) {
      this.listSubscription.unsubscribe();
    }

    // Clearing immediately prevents showing the previous client's audited records.
    this.tableData = [];
    this.totalRecord = 0;
    this.totalPagesArray = [];
    this.errorMessage = '';

    const clientId = this.currentClientId;
    if (!clientId || clientId <= 0) {
      this.loading = false;
      this.errorMessage = 'Select a client to view its audited group plans.';
      return;
    }

    const params: {
      clientId: number;
      page: number;
      size: number;
      groupNumber?: string;
      planName?: string;
      benefitDomain?: BenefitDomain;
    } = {
      clientId,
      page: this.pageIndex,
      size: this.pageSize
    };

    const groupNumber = this.appliedGroupNumber;
    const planName = this.appliedPlanName;

    if (groupNumber) {
      params.groupNumber = groupNumber;
    }
    if (planName) {
      params.planName = planName;
    }
    if (this.appliedBenefitDomain) {
      params.benefitDomain = this.appliedBenefitDomain;
    }

    this.loading = true;
    this.listSubscription = this.insuranceService.getAuditedRepository(params).subscribe(
      (response: AuditedRepositoryPage) => {
        this.ngZone.run(() => {
          if (this.currentClientId !== clientId) {
            return;
          }

          this.tableData = response && Array.isArray(response.content) ? response.content : [];
          const totalElements = Number(response && response.totalElements);
          this.totalRecord = Number.isFinite(totalElements) ? totalElements : this.tableData.length;

          const totalPages = Math.ceil(this.totalRecord / this.pageSize);
          this.totalPagesArray = Array.from({length: totalPages}, (_, index) => index + 1);
          this.loading = false;
          // Without this, API data may only appear after the next unrelated click.
          this.cdr.detectChanges();
        });
      },
      (error: any) => {
        this.ngZone.run(() => {
          if (this.currentClientId !== clientId) {
            return;
          }

          this.loading = false;
          this.errorMessage = this.getErrorMessage(error, 'Unable to load audited group plans.');
          this.cdr.detectChanges();
        });
      }
    );
  }

  viewPlan(plan: AuditedGroupPlan): void {
    this.openDetailsById(plan.groupId, plan);
  }

  openDetailsById(groupId: number, summary?: AuditedGroupPlan): void {
    if (!Number.isFinite(groupId) || groupId <= 0) {
      return;
    }

    this.closeFilters();

    if (this.detailsSubscription) {
      this.detailsSubscription.unsubscribe();
    }

    this.detailsVisible = true;
    this.detailsLoading = true;
    this.detailsError = '';
    this.selectedGroupId = groupId;
    this.selectedPlan = summary || this.tableData.find(plan => plan.groupId === groupId) || null;
    this.detailSections = [];

    const clientId = this.currentClientId;
    if (!clientId) {
      this.detailsLoading = false;
      this.detailsError = 'Select a client to view audited plan details.';
      return;
    }

    this.detailsSubscription = this.insuranceService.getAuditedRepositoryDetail(groupId, clientId).subscribe(
      (details: AuditedGroupPlanDetail) => {
        this.ngZone.run(() => {
          if (this.currentClientId !== clientId || !this.detailsVisible || this.selectedGroupId !== groupId) {
            return;
          }

          if (!details) {
            this.detailsError = 'No audited details were returned for this group plan.';
          } else {
            this.selectedPlan = details;
            this.detailSections = this.buildDetailSections(details);
          }
          this.detailsLoading = false;
          this.cdr.detectChanges();
        });
      },
      (error: any) => {
        this.ngZone.run(() => {
          if (this.currentClientId !== clientId || !this.detailsVisible || this.selectedGroupId !== groupId) {
            return;
          }
          this.detailsLoading = false;
          this.detailsError = this.getErrorMessage(error, 'Unable to load audited plan details.');
          this.cdr.detectChanges();
        });
      }
    );
  }

  retryDetails(): void {
    if (this.selectedGroupId !== null) {
      this.openDetailsById(this.selectedGroupId, this.selectedPlan);
    }
  }

  closeDetails(navigateToRepository = true): void {
    if (this.detailsSubscription) {
      this.detailsSubscription.unsubscribe();
    }
    this.detailsVisible = false;
    this.detailsLoading = false;
    this.detailsError = '';
    this.selectedGroupId = null;
    this.selectedPlan = null;
    this.detailSections = [];

    if (navigateToRepository && this.deepLinkGroupId !== null) {
      this.router.navigate(['/repository']);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.filtersExpanded) {
      this.closeFilters();
    } else if (this.detailsVisible) {
      this.closeDetails();
    }
  }

  private buildDetailSections(details: AuditedGroupPlanDetail): AuditedDetailSection[] {
    const metadata = [
      'groupId', 'clientId', 'groupNumber', 'planName',
      'planType', 'insuranceName', 'audit'
    ];

    // Render every benefit field returned by the detail API. This includes any
    // new coverage, history, or patient-info sections without UI schema changes.
    return Object.keys(details)
      .filter(key => metadata.indexOf(key) === -1)
      .map(key => {
        const value = details[key];
        const items = Array.isArray(value) ? value : (value === null || value === undefined ? [] : [value]);

        return {
          title: this.formatLabel(key),
          records: items.map((item: any, index: number) => ({
            title: items.length > 1 ? 'Record ' + (index + 1) : '',
            fields: this.flattenFields(item)
          }))
        };
      });
  }

  private flattenFields(value: any, prefix = ''): AuditedDetailField[] {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return [{label: prefix || 'Value', value: '—'}];
      }
      return value.reduce(
        (fields: AuditedDetailField[], item: any, index: number) =>
          fields.concat(this.flattenFields(item, (prefix ? prefix + ' ' : '') + '[' + (index + 1) + ']')),
        []
      );
    }

    if (value !== null && typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return [{label: prefix || 'Value', value: '—'}];
      }
      return keys.reduce(
        (fields: AuditedDetailField[], key: string) =>
          fields.concat(this.flattenFields(value[key],
            prefix ? prefix + ' / ' + this.formatLabel(key) : this.formatLabel(key))),
        []
      );
    }

    return [{label: prefix || 'Value', value: this.formatValue(value)}];
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  }

  private formatLabel(value: string): string {
    return value
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, match => match.toUpperCase());
  }

  private getErrorMessage(error: any, fallback: string): string {
    const apiMessage = error && error.error && error.error.message;
    return typeof apiMessage === 'string' && apiMessage.trim() ? apiMessage : fallback;
  }
}
